import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Section } from './Shared';
type Slide = {
  src: string;
  alt: string;
  title: string;
  caption: string;
};
const slides: Slide[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/uunVEPRwoyjBGsZTfwjieC/Screenshot_20260528-112429~2.png',
  alt: 'Ficha técnica — Acción farmacológica e indicaciones',
  title: 'VOLUMEN I · ENCICLOPEDIA GLOBAL A-L',
  caption: 'Fichas técnicas completas con semáforo de seguridad'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/kfTJBTFpsHwtimJA1GSPsZ/Screenshot_20260528-113009~2.png',
  alt: 'Protocolos por sistema: sistema digestivo',
  title: 'VOLUMEN IV · PROTOCOLOS CLÍNICOS',
  caption: 'Mapas terapéuticos por sistema con plantas de acción específica'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/sVQ7VbiChq5kj6BL9NhPyM/Screenshot_20260528-112733~2.png',
  alt: 'Paso 2: la calcinación — separación de la sal',
  title: 'VOLUMEN III · PREPARACIONES Y GALÉNICA',
  caption: 'Procesos paso a paso para elaborar tinturas, sales y extractos'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/i1Wbywum6wy1HwRuBVGvJh/Screenshot_20260528-112610~2.png',
  alt: 'Fórmula magistral lista para usar',
  title: 'BONUS · VADEMÉCUM GLOBAL DE FÓRMULAS',
  caption: '50 fórmulas listas para usar en consulta, con tabla de sustituciones'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/aFBx9cK3C5mzfBmDs83xuK/Screenshot_20260812-195822~2.jpg',
  alt: 'Volumen V — Alquimia vegetal y espagiria moderna',
  title: 'VOLUMEN V · ALQUIMIA VEGETAL',
  caption: 'Espagiria moderna y fitococina terapéutica por sistema'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/xg1nrR5aina9jytz5fxJsq/Screenshot_20260528-112522~2.png',
  alt: 'Hábitos y estilo de vida — ficha de paciente',
  title: 'BONUS · EL LEGAJO DE CAMPO',
  caption: 'Planillas clínicas profesionales para evaluación integral del paciente'
}];
export const MaterialPreview = () => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const goTo = useCallback((next: number) => {
    setDirection(next > index ? 1 : -1);
    setIndex((next + slides.length) % slides.length);
  }, [index]);
  const next = useCallback(() => {
    setDirection(1);
    setIndex(i => (i + 1) % slides.length);
  }, []);
  const prev = useCallback(() => {
    setDirection(-1);
    setIndex(i => (i - 1 + slides.length) % slides.length);
  }, []);
  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [next, paused]);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX === null || slides.length <= 1) return;
    const delta = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(delta) > 40) {
      if (delta < 0) next();else prev();
    }
    setTouchStartX(null);
  };
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 80 : -80,
      opacity: 0,
      scale: 0.96
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -80 : 80,
      opacity: 0,
      scale: 0.96
    })
  };
  return <Section className="max-w-5xl" data-id="element-217" data-mp="1gz09mh41yvxj">
      <div className="text-center mb-8 md:mb-12" data-id="element-218" data-mp="1gyqe6e08nwkg">
        <div className="inline-flex items-center justify-center bg-white/80 border border-brand-purple/40 px-4 py-1.5 rounded-full mb-5 shadow-sm" data-id="element-219" data-mp="1cxn2m3p84mp1">
          <span className="font-serif font-semibold tracking-wider text-brand-purpleDark text-xs md:text-sm" data-id="element-220" data-mp="180eihxkim8l5">
            VISTA PREVIA DEL PACK
          </span>
        </div>
        <h2 className="text-2xl md:text-4xl lg:text-5xl font-display font-bold leading-tight text-brand-purpleDark" data-id="element-221" data-mp="32cl6jwyicjuv">
          📸 Conoce el material por dentro
        </h2>
        <p className="text-brand-text/70 mt-4 text-sm md:text-base max-w-2xl mx-auto" data-id="element-222" data-mp="240s1cfkbnzl8">
          5 volúmenes, bonus y planillas clínicas — todo diseñado para usar en el
          consultorio desde la primera consulta.
        </p>
      </div>

      <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-223" data-mp="1uz0wfcxlvwsf">
        {/* Video Preview */}
        <div className="mb-8 rounded-3xl overflow-hidden border border-brand-purple/20 shadow-[0_10px_30px_rgba(23,61,31,0.15)] bg-black" data-id="element-224" data-mp="1g9mmo3q4bdci">
          <video src="https://cdn.magicpatterns.com/uploads/aXXPHHKW3BPH1mzduAMmHT/video_para_landing_MAGIC_optimizado.mp4" className="w-full h-auto max-h-[600px] object-contain" autoPlay muted loop playsInline controls data-id="element-225" data-mp="17bkft7jpdqhb" />
        </div>

        <div className="relative rounded-3xl overflow-hidden border border-brand-purple/30 bg-gradient-to-br from-brand-purpleDark/95 via-brand-purple/90 to-brand-greenMoss/90 shadow-[0_20px_60px_rgba(45,95,63,0.25)]" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-id="element-226" data-mp="xddbppaij2wq">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay pointer-events-none" data-id="element-227" data-mp="druxxst5gup4"></div>

          

          {slides.length > 1 && <>
              <button type="button" onClick={prev} aria-label="Anterior" className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/95 border border-brand-purple/40 shadow-md flex items-center justify-center text-brand-purple hover:bg-white hover:scale-105 active:scale-95 transition-all z-10" data-id="element-228" data-mp="nnejbla38azm">
                <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" data-id="element-229" data-mp="16fqv0ymmsomf" />
              </button>
              <button type="button" onClick={next} aria-label="Siguiente" className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/95 border border-brand-purple/40 shadow-md flex items-center justify-center text-brand-purple hover:bg-white hover:scale-105 active:scale-95 transition-all z-10" data-id="element-230" data-mp="1kf8bmqprwvkp">
                <ChevronRight className="w-5 h-5 md:w-6 md:h-6" data-id="element-231" data-mp="1sgppu6o1jd4r" />
              </button>
            </>}
        </div>

        <div className="mt-5 text-center px-4 min-h-[2.5rem]" data-id="element-232" data-mp="1xdg2ek0mzqlf">
          <AnimatePresence mode="wait" data-id="element-233" data-mp="225zgrj75epra">
            
          </AnimatePresence>
        </div>

        {slides.length > 1 && <>
            
            
          </>}
      </div>
    </Section>;
};